import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { defaultValue, IEleve } from 'app/shared/model/eleve.model';
import { InscriptionDTO } from 'app/shared/model/inscription.model';
import { IPointage } from 'app/shared/model/pointage.model';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';

export type IEleveQueryParams = { eleveId?: string; anneeScolaireId?: string; query?: string; page?: number; size?: number; sort?: string };
import { IRootState } from 'app/config/store';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IEleve>,
  entity: defaultValue,
  entitiesInscriptions: [] as ReadonlyArray<InscriptionDTO>,
  entitiesPointages: [] as ReadonlyArray<IPointage>,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
  totalPointages: 0,
  searchCriteria: defaultValue,
  lastListPage: '',
};

const apiUrl = 'api/eleves';

// Actions

export const getEleves = createAsyncThunk('eleve/fetch_eleve_list', async (entity: any) => {
  let requestUrl = '';
  let etatPaiement;
  let statutPresence;
  let matricule;
  let nom, prenom;
  let classe;
  let searchUrl = '';
  if (entity.matricule) {
    matricule = '&matricule=' + entity.matricule;
    searchUrl += matricule;
  }
  if (entity.nom) {
    nom = '&nom=' + entity.nom;
    searchUrl += nom;
  }
  if (entity.prenom) {
    prenom = '&prenom=' + entity.prenom;
    searchUrl += prenom;
  }
  if (entity.classe) {
    classe = '&classe=' + entity.classe;
    searchUrl += classe;
  }
  if (entity.etatPaiement) {
    etatPaiement = '&etatPaiement=' + entity.etatPaiement;
    searchUrl += etatPaiement;
  }
  if (entity.statutPresence) {
    statutPresence = '&statutPresence=' + entity.statutPresence;
    searchUrl += statutPresence;
  }
  if (!entity.matricule && !entity.nom && !entity.prenom && !entity.classe && !entity.etatPaiement && !entity.statutPresence) {
    requestUrl = `${apiUrl}?ecoleId=${entity.ecoleId}&anneeScolaireId=${entity.anneeScolaireId}&page=${entity.page}&size=${entity.size}`;
  } else {
    requestUrl = `${apiUrl}?ecoleId=${entity.ecoleId}&anneeScolaireId=${entity.anneeScolaireId}&page=${entity.page}&size=${entity.size}${
      searchUrl ? '&' : '?'
    }${searchUrl}`;
  }
  return axios.get<IEleve[]>(requestUrl);
});
export const getFicheEleves = createAsyncThunk('eleve/fetch_fiche_eleve_list', async (entity: any) => {
  let requestUrl = '';
  let etatPaiement;
  let statutPresence;
  let matricule;
  let nom, prenom;
  let classe;
  let searchUrl = '';
  if (entity.matricule) {
    matricule = '&matricule=' + entity.matricule;
    searchUrl += matricule;
  }
  if (entity.nom) {
    nom = '&nom=' + entity.nom;
    searchUrl += nom;
  }
  if (entity.prenom) {
    prenom = '&prenom=' + entity.prenom;
    searchUrl += prenom;
  }
  if (entity.classe) {
    classe = '&classe=' + entity.classe;
    searchUrl += classe;
  }
  if (entity.etatPaiement) {
    etatPaiement = '&etatPaiement=' + entity.etatPaiement;
    searchUrl += etatPaiement;
  }
  if (entity.statutPresence) {
    statutPresence = '&statutPresence=' + entity.statutPresence;
    searchUrl += statutPresence;
  }
  if (!entity.matricule && !entity.nom && !entity.prenom && !entity.classe && !entity.etatPaiement && !entity.statutPresence) {
    requestUrl = `${`api/fiche-eleves`}?ecoleId=${entity.ecoleId}&page=${entity.page}&size=${entity.size}`;
  } else {
    requestUrl = `${`api/fiche-eleves`}?ecoleId=${entity.ecoleId}&page=${entity.page}&size=${entity.size}${
      searchUrl ? '&' : '?'
    }${searchUrl}`;
  }
  return axios.get<IEleve[]>(requestUrl);
});

export const getEleve = createAsyncThunk('eleve/fetch_eleve', async (id: string | number, thunkAPI) => {
  const requestUrl = `${apiUrl}/${id}`;
  thunkAPI.dispatch(getPhoto(id));
  return axios.get<IEleve>(requestUrl);
});

export const getEleveByAnneeScolaire = createAsyncThunk('eleve/fetch_eleve', async (entity: IEleve, thunkAPI) => {
  const requestUrl = `${apiUrl}-annee/${entity.id}?anneeScolaireId=${entity.anneeScolaireId}`;
  thunkAPI.dispatch(getPhoto(entity.id));
  return axios.get<IEleve>(requestUrl);
});
export const getEleveInscriptions = createAsyncThunk('eleve/fetch_eleve_inscriptions', async (eleveId: string) => {
  const requestUrl = `api/inscriptions/eleve/${eleveId}`;
  return axios.get<InscriptionDTO[]>(requestUrl);
});
export const getElevePointages = createAsyncThunk(
  'eleve/fetch_eleve_pointages',
  async ({ eleveId, anneeScolaireId, page = 0, size = ITEMS_PER_PAGE }: IEleveQueryParams) => {
    const requestUrl = `${apiUrl}/${eleveId}/allPointages?anneeScolaireId=${anneeScolaireId}&page=${page}&size=${size}`;

    return axios.get<IPointage[]>(requestUrl);
  }
);
export const createEleveInscriptions = createAsyncThunk('eleve/create_eleve_inscriptions', async (data: InscriptionDTO, thunkAPI) => {
  const requestUrl = `api/inscriptions`;
  const result = await axios.post<InscriptionDTO>(requestUrl, data);
  thunkAPI.dispatch(getEleveInscriptions(result.data.eleveId));
  return result;
});

export const deleteInscription = createAsyncThunk(
  'eleve/delete_inscription',
  async (inscriptionId: string, thunkAPI) => {
    const requestUrl = `api/inscriptions/${inscriptionId}`;
    const result = await axios.delete<void>(requestUrl);
    const state = thunkAPI.getState() as IRootState;
    const eleveId = state.eleve.entity.id;
    if (eleveId) {
      thunkAPI.dispatch(getEleveInscriptions(eleveId));
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getElevesByClasse = createAsyncThunk('eleve/fetch_eleves_classe', async (entity: any) => {
  const requestUrl = `${apiUrl}?ecoleId=${entity.ecoleId}&page=${entity.page}&size=${entity.size}&classe=${entity.classeId}`;
  return axios.get<IEleve[]>(requestUrl);
});

export const getElevesByParent = createAsyncThunk(
  'eleve/fetch_eleve_parents',
  async (entity: any) => {
    const url = 'api/parents';
    const requestUrl = `${url}/${entity.id}/ecole?ecoleId=${entity.ecoleId}`;
    return axios.get<IEleve[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getPhoto = createAsyncThunk(
  'eleve/fetch_photo',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}/photo`;
    return axios.get<string>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const uploadPaiements = createAsyncThunk(
  'eleve/upload_paiements',
  async (entity: any, thunkAPI) => {
    const result = await axios.post<IEleve>(`${apiUrl}/upload-paiements?ecoleId=${entity.ecoleId}`, entity.file, {
      headers: { 'content-type': 'multipart/form-data' },
    });
    thunkAPI.dispatch(getEleves({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);
export const uploadEleveList = createAsyncThunk(
  'eleve/init_upload',
  async (entity: any, thunkAPI) => {
    const result = await axios.post<IEleve>(
      `${apiUrl}/init-upload?ecoleId=${entity.ecoleId}&modeInscription=${entity.modeInscription || 'AUCUNE'}&anneeScolaireId=${
        entity.anneeScolaireId || ''
      }`,
      entity.file,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    thunkAPI.dispatch(getEleves(entity));
    const ficheEntity = {
      ecoleId: entity.ecoleId,
      page: 0,
      size: ITEMS_PER_PAGE,
    };
    thunkAPI.dispatch(getFicheEleves(ficheEntity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateElevesUploadList = createAsyncThunk(
  'eleve/update_eleves_upload',
  async (entity: any, thunkAPI) => {
    const result = await axios.post<IEleve>(`${apiUrl}/update-upload?ecoleId=${entity.ecoleId}`, entity.file, {
      headers: { 'content-type': 'multipart/form-data' },
    });
    thunkAPI.dispatch(getEleves(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createEleve = createAsyncThunk(
  'eleve/create_eleve',
  async (entity: IEleve, thunkAPI) => {
    const result = await axios.post<IEleve>(
      `${apiUrl}?ecoleId=${entity.ecoleId}&inscrireEleve=${entity.createInscription}`,
      cleanEntity(entity)
    );
    thunkAPI.dispatch(getEleves({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const addPhoto = createAsyncThunk(
  'eleve/add_photo',
  async (entity: any, thunkAPI) => {
    const response = await axios.post<IEleve>(`${apiUrl}/${entity.id}/photo`, entity.photo, {
      headers: { 'content-type': 'multipart/form-data' },
    });
    thunkAPI.dispatch(getEleves({ entity }));
    return response;
  },
  { serializeError: serializeAxiosError }
);

export const updateEleve = createAsyncThunk(
  'eleve/update_eleve',
  async (entity: IEleve, thunkAPI) => {
    const result = await axios.put<IEleve>(
      `${apiUrl}/${entity.id}?ecoleId=${entity.ecoleId}&updateInscription=${entity.updateInscription}`,
      cleanEntity(entity)
    );
    thunkAPI.dispatch(getEleves({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updatePaiementEleve = createAsyncThunk(
  'eleve/update_paiement_inscription',
  async (entity: IEleve, thunkAPI) => {
    const result = await axios.patch<IEleve>(
      `api/inscriptions/update/${entity.id}`,
      {
        id: `${entity.id}`,
        etatPaiement: `${entity.etatPaiement}`,
      },
      {
        headers: {
          'Content-Type': 'application/merge-patch+json',
        },
      }
    );
    thunkAPI.dispatch(getEleve(entity.id));
    thunkAPI.dispatch(getPhoto(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateSendStatusFacture = createAsyncThunk(
  'eleve/update-status_facture-eleve',
  async (entity: IEleve, thunkAPI) => {
    const result = await axios.patch<IEleve>(
      `${apiUrl}/${entity.id}`,
      {
        id: `${entity.id}`,
        statutEnvoiFacture: `${entity.statutEnvoiFacture}`,
      },
      {
        headers: {
          'Content-Type': 'application/merge-patch+json',
        },
      }
    );
    thunkAPI.dispatch(getEleve(entity.id));
    thunkAPI.dispatch(getPhoto(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEleve = createAsyncThunk(
  'eleve/delete_eleve',
  async (entity: IEleve, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<IEleve>(requestUrl);
    thunkAPI.dispatch(getFicheEleves(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const unlinkSupportEleve = createAsyncThunk(
  'eleve/unlink_eleve_support',
  async (entity: any, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}/unlink-support`;
    const result = await axios.delete<IEleve>(requestUrl);
    thunkAPI.dispatch(getEleve(entity.id));
    thunkAPI.dispatch(getPhoto(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);
export const resetPaymentStatus = createAsyncThunk(
  'eleve/reset_payment_status',
  async (entity: any, thunkAPI) => {
    const requestUrl = `${apiUrl}/reset-statut/${entity.ecoleId}`;

    const result = await axios.post(requestUrl);
    thunkAPI.dispatch(getEleves(entity));

    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getMatricules = createAsyncThunk(
  'eleve/fetch_matricules',
  async (ecoleId: string) => {
    const requestUrl = `${apiUrl}/matricules/${ecoleId}`;
    const response = await axios.get<string[]>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const deleteAbsenceEleve = createAsyncThunk(
  'eleve/delete_absence_eleve',
  async (entity: { absenceId: string; eleveId: string; anneeScolaireId: string }, thunkAPI) => {
    const requestUrl = `api/absences/eleve/${entity.absenceId}`;
    const result = await axios.delete<void>(requestUrl);
    // Rafraîchir les données de l'élève après suppression
    thunkAPI.dispatch(getEleveByAnneeScolaire({ id: entity.eleveId, anneeScolaireId: entity.anneeScolaireId } as IEleve));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice
export type EleveState = Readonly<typeof initialState>;
export const EleveSlice = createSlice({
  name: 'eleve',
  initialState: initialState as EleveState,
  reducers: {
    reset() {
      return initialState;
    },
    setSearchCriteria(state, action) {
      state.searchCriteria = action.payload;
    },
    setLastListPage(state, action) {
      state.lastListPage = action.payload;
    },
    resetSearchCriteria(state) {
      state.searchCriteria = defaultValue;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEleve.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEleveInscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.entitiesInscriptions = action.payload.data;
      })
      .addCase(getElevePointages.fulfilled, (state, action) => {
        state.loading = false;
        state.entitiesPointages = action.payload.data;
        state.totalPointages = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addCase(getPhoto.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.photo = action.payload.data;
      })
      .addCase(deleteEleve.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addCase(deleteInscription.pending, state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addCase(deleteInscription.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.loading = false;
      })
      .addCase(deleteInscription.rejected, (state, action) => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = false;
        state.errorMessage = action.error.message || 'Erreur lors de la suppression';
      })
      .addCase(deleteAbsenceEleve.pending, state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addCase(deleteAbsenceEleve.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.loading = false;
      })
      .addCase(deleteAbsenceEleve.rejected, (state, action) => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = false;
        state.errorMessage = action.error.message || "Erreur lors de la suppression de l'absence";
      })
      .addMatcher(isFulfilled(getEleves, getFicheEleves), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getElevesByClasse), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(resetPaymentStatus), (state, action) => {
        return {
          ...state,
          loading: false,
        };
      })
      .addMatcher(isFulfilled(createEleve, updateEleve), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(createEleveInscriptions), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })

      .addMatcher(isFulfilled(getElevesByParent), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(
        isPending(
          getEleves,
          getFicheEleves,
          getEleve,
          getElevesByParent,
          resetPaymentStatus,
          getEleveInscriptions,
          getElevePointages,
          createEleveInscriptions
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      )
      .addMatcher(isPending(createEleve, updateEleve, deleteEleve, resetPaymentStatus), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(createEleveInscriptions), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = false;
        state.loading = false;
      });
  },
});

export const { reset, setSearchCriteria, resetSearchCriteria, setLastListPage } = EleveSlice.actions;

// Reducer
export default EleveSlice.reducer;
